import { Injectable } from '@angular/core';
import {
    Application,
    ApplicationHDNRequest,
    ApplicationHDNResponse,
    CordFinanceService,
    CordHdnService,
    CreateApplicationResponse, GdprRequest,
    HdnRecord,
    HdnRecordPatch
} from '@yellowtail-frontend/cord-connector';
import { StorageService } from '@yellowtail-frontend/storage-service';
import { StorageObjects, UserType } from '@enums';
import { HttpService } from'../../services/utilities/http.service';
import { FileResponse } from 'src/app/models/component.model';
import { GenerateApplicationPdfRequest } from 'src/app/models/application/wrapper.model';
import { HdnConfigurationModel } from 'src/app/models/application/hdnConfiguration';

@Injectable({
    providedIn: 'root'
})
export class ApplicationService {

    constructor(
        private readonly _cordFinanceService: CordFinanceService,
        private readonly _storageService: StorageService,
        private readonly _httpService: HttpService,
        private readonly _cordHdnService: CordHdnService
    ) { }

    createApplication(userId: string): Promise<CreateApplicationResponse> {
        return this._cordFinanceService.createApplicationWithConsent(userId);
    }

    async createApplicationAssignedToAdmin(): Promise<string> {
        const USER_ID = this._storageService.get<string>(StorageObjects.userId);
        const USER_TYPE = this._storageService.get<string>(StorageObjects.userType);
        const IS_ADMIN = USER_TYPE === UserType[UserType.SuperUser] || USER_TYPE === UserType[UserType.SystemAdmin];
        const { ApplicationID } = await this.createApplication(USER_ID);
        if (IS_ADMIN) {
            await this.assignDossierToAdmin(USER_ID, ApplicationID);
        }
        return ApplicationID;
    }

    assignDossierToAdmin(adminId: string, applicationId: string): Promise<string> {
        return this._cordFinanceService.assignAdminToApplication(adminId, applicationId);
    }

    getApplicationById(applicationId: string): Promise<Application> {
        return this._cordFinanceService.getApplicationById(applicationId);
    }

    updateApplication(data: Application): Promise<Application> {
        return this._cordFinanceService.updateApplication(data);
    }

    closeApplicationDossier(applicationId: string): Promise<Application> {
        return this._cordFinanceService.closeApplicationDossier(applicationId);
    }

    getHdnCOnfigurationData(): Promise<HdnConfigurationModel> {
        return (this._httpService.get<HdnConfigurationModel>(`application/hdnRecordData`)).toPromise();
    }

    postHdnRecordData(hdnRecordData: HdnRecord): Promise<void> {
        return this._cordHdnService.createHdnRecord(hdnRecordData);
    }

    patchHdnRecordData(hdnRecordData: HdnRecordPatch[], applicationId: string): Promise<void> {
        return this._cordHdnService.patchHdnRecordApplication(applicationId, hdnRecordData);
    }

    // Defined by CORD as a PATCH endpoint, used to update the Application.HdnData object,
    // HOWEVER this is not a true PATCH, since data not passed in request will be overwritten.
    updateApplicationHDNData(data: ApplicationHDNRequest, applicationId: string): Promise<ApplicationHDNResponse> {
        return this._cordFinanceService.updateApplicationHDN(data, applicationId);
    }

    authorizeApplication(data: GdprRequest): Promise<void> {
        return this._cordFinanceService.saveConsent(data);
    }

    revokeApplication(data: GdprRequest): Promise<void> {
        return this._cordFinanceService.revokeApplication(data);
    }

    generatePdf(data: GenerateApplicationPdfRequest): Promise<FileResponse> {
        return (this._httpService.post<FileResponse>(`application/generatePdf`, data)).toPromise();
    }

    // DELETE
    deletePerson(id: string): Promise<void> {
        return this._cordFinanceService.deletePerson(id);
    }

    deleteLegalPerson(id: string): Promise<void> {
        return this._cordFinanceService.deleteLegalPerson(id);
    }

    deleteRealEstate(id: string): Promise<void> {
        return this._cordFinanceService.deleteRealEstate(id);
    }

    deleteMortgage(id: string): Promise<void> {
        return this._cordFinanceService.deleteMortgage(id);
    }

    deleteLoanPart(id: string, applicationId: string): Promise<void> {
        return this._cordFinanceService.deleteLoanPart(id, applicationId);
    }

    deleteLoan(id: string): Promise<void> {
        return this._cordFinanceService.deleteLoan(id);
    }

}
